import React, { useCallback, useMemo } from "react";
import { defineMessage, defineMessages } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyLocales } from "../../components/property-locale-provider";
import schema from "../../gen/schema/witbooking.backoffice.mealplan.v1/MealPlan.json";
import { MealPlanAPIPromiseClient } from "../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_grpc_web_pb";
import {
  CreateMealPlanRequest,
  DeleteMealPlanRequest,
  GetAllMealPlansRequest,
  GetMealPlanRequest,
  MealPlan,
  Translation,
  UpdateMealPlanRequest,
} from "../../gen/witbooking/backoffice/mealplan/v1/mealplan_api_pb";
import actions from "../../lib/actions";
import { useUserAuthInfo } from "../../lib/auth";
import { getLanguageFromFullLocale } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import * as TableRenderers from "../shared/renderers/table";
import { HtmlEditorWidget } from "../shared/widgets/html";
import { translationFieldConfig } from "../shared/widgets/translation";

import { FormContainer } from "./form-style";

// //TODO: These methods could be generated automatically.
function mealPlanFromObject(obj: MealPlan.AsObject): MealPlan {
  const result = new MealPlan();
  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setTranslationsList(obj.translationsList.map(translationFromObject));
  result.setMapTo(obj.mapTo);

  return result;
}

function translationFromObject(obj: Translation.AsObject): Translation {
  const result = new Translation();
  result.setLocale(obj.locale);
  result.setName(obj.name);
  result.setDescription(obj.description);

  return result;
}

const mealplanMessages = defineMessages({
  entityName: {
    id: "mealplan.entityName",
    defaultMessage: "Meal Plan",
  },
  mappingNone: {
    id: "mealplan.mapping.none",
    defaultMessage: "No Mapping",
  },
  mappingRoomOnly: {
    id: "mealplan.mapping.roomOnly",
    defaultMessage: "RO",
  },
  mappingBedAndBreakfast: {
    id: "mealplan.mapping.bedAndBreakfast",
    defaultMessage: "BO",
  },
  mappingHalfBoard: {
    id: "mealplan.mapping.halfBoard",
    defaultMessage: "HB",
  },
  mappingFullBoard: {
    id: "mealplan.mapping.fullBoard",
    defaultMessage: "FB",
  },
  mappingAllInclusive: {
    id: "mealplan.mapping.allInclusive",
    defaultMessage: "AI",
  },
});

const tableHeaderMessages = defineMessages({
  ticker: { id: "mealplan.ticker", defaultMessage: "Identifier" },
  translationsList: { id: "mealplan.name", defaultMessage: "Name" },
});

const messages = {
  ...defaultCrudMessages,
  ...mealplanMessages,
  patternValidationFailed: {
    ".ticker": defineMessage({
      id: "mealplan.tickerPatternFailed",
      defaultMessage: "Must be uppercase. Doesn't allow blank spaces or special characters",
    }),
  },
  tableHeaders: tableHeaderMessages,
  deleteDisabledDescription: defineMessage({
    id: "mealplan.deleteDisabledDescription",
    defaultMessage: "This Meal Plan has linked inventory lines, so it can't be deleted.",
  }),
};

interface MealPlanFilters {
  ticker: string;
}

function MealPlanCrud({ propertyTicker }: { propertyTicker: string }) {
  const client = useGrpcClient(MealPlanAPIPromiseClient);
  const getAllMealPlans = usePropertyGrpcCall(client, client.getAllMealPlans);
  const deleteMealPlan = usePropertyGrpcCall(client, client.deleteMealPlan);
  const getMealPlan = usePropertyGrpcCall(client, client.getMealPlan);
  const createMealPlan = usePropertyGrpcCall(client, client.createMealPlan);
  const updateMealPlan = usePropertyGrpcCall(client, client.updateMealPlan);

  const onGetAll = useCallback(async () => {
    const request = new GetAllMealPlansRequest();
    request.setPropertyTicker(propertyTicker);
    const response = await getAllMealPlans(request);
    return response.getMealPlansList().map(m => m.toObject());
  }, [propertyTicker, getAllMealPlans]);

  const onDelete = useCallback(
    async (id: string) => {
      const request = new DeleteMealPlanRequest();
      request.setPropertyTicker(propertyTicker);
      request.setId(id);
      await deleteMealPlan(request);
    },
    [propertyTicker, deleteMealPlan]
  );

  const onGetOne = useCallback(
    async id => {
      const request = new GetMealPlanRequest();
      request.setPropertyTicker(propertyTicker);
      request.setId(id);
      const response = (await getMealPlan(request)).getMealPlan();
      if (response) return response.toObject();
      else throw new Error("Unexpected reply obtaining mealplan");
    },
    [propertyTicker, getMealPlan]
  );

  const onCreate: (entity: MealPlan.AsObject) => Promise<string> = useCallback(
    async entity => {
      const mealPlan = mealPlanFromObject(entity);
      const request = new CreateMealPlanRequest();
      request.setPropertyTicker(propertyTicker);
      request.setMealPlan(mealPlan);

      const response = (await createMealPlan(request)).getMealPlan();
      if (response) return response.getId();
      else throw new Error("Unexpected reply during creation.");
    },
    [propertyTicker, createMealPlan]
  );

  const onUpdate: (entity: MealPlan.AsObject) => Promise<void> = useCallback(
    async entity => {
      const mealPlan = mealPlanFromObject(entity);
      const request = new UpdateMealPlanRequest();
      request.setPropertyTicker(propertyTicker);
      request.setMealPlan(mealPlan);
      await updateMealPlan(request);
    },
    [propertyTicker, updateMealPlan]
  );

  const user = useUserAuthInfo();
  const isAdmin =
    user?.isWitAdmin ||
    user?.isC2tAdmin ||
    user?.isCampingAdmin ||
    user?.isCampingFrAdmin ||
    user?.isAciAdmin;
  const allowedLocales = usePropertyLocales();
  const formConfiguration: FormConfiguration<MealPlan.AsObject> = useMemo(
    () => ({
      __fieldOrder: ["ticker", "mapTo", "translationsList", "*"],
      __useProtoStyleArrays: true,
      id: {
        default: () => uuidv4(),
        hidden: true,
      },
      ticker: {
        readonly: isNew => !isNew,
        messages: {
          label: tableHeaderMessages.ticker,
          helpText: defineMessage({
            id: "mealplan.ticker.help",
            defaultMessage: "In capital letters and 3 letters maximum",
          }),
        },
      },
      inUse: {
        hidden: true,
      },
      mapTo: {
        default: "",
        transform: v => (v === "" ? undefined : v),
        hidden: isNew => !isNew && !isAdmin,
        enumLabels: [
          messages.mappingRoomOnly,
          messages.mappingBedAndBreakfast,
          messages.mappingHalfBoard,
          messages.mappingFullBoard,
          messages.mappingAllInclusive,
        ],
        messages: {
          label: defineMessage({ id: "mealplan.mapTo", defaultMessage: "Map to" }),
        },
      },
      translationsList: {
        ...translationFieldConfig(allowedLocales || []),
        children: {
          __fieldOrder: ["name", "description", "*"],
          name: {
            messages: {
              label: defineMessage({ id: "mealplan.translation.name", defaultMessage: "Name" }),
            },
          },
          description: {
            widget: HtmlEditorWidget,
            messages: {
              label: defineMessage({
                id: "mealplan.translation.description",
                defaultMessage: "Description",
              }),
            },
          },
          ...translationFieldConfig(allowedLocales || []).children,
        },
        messages: {
          label: defineMessage({ id: "mealplan.translations", defaultMessage: "Translations" }),
        },
      },
    }),
    [allowedLocales, isAdmin]
  );

  const disableDelete = useCallback(
    (value: MealPlan.AsObject) => value.inUse && "mealplan.inUse",
    []
  );

  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <SimpleCrud<MealPlan.AsObject, MealPlanFilters>
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={{
        ticker: {
          renderer: TableRenderers.StringRenderer,
          width: 140,
        },
        translationsList: TableRenderers.mapRenderer(TableRenderers.StringRenderer, (v, intl) => {
          if (!v || v.length === 0) return undefined;
          const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
          return current?.name || v[0].name;
        }),
      }}
      // tableFilters={{
      //This is a sync filter. It will filter already loaded data.
      // ticker: {
      //   filterFn: (data, filter) => data.toLowerCase().includes(filter.toLowerCase()),
      //   widget: ({value, onChange}) => <Form.Control value={value} onChange={(e: any) => onChange(e.target.value)} />
      // },
      //This is an async filter. It will trigger a call to onGetAll with the new filter values whenever it changes
      // ticker: {
      //   filterFn: "async",
      //   widget: ({value, onChange}) => <Form.Control value={value} onChange={(e: any) => onChange(e.target.value)} />
      // },
      // }}
      formContainer={FormContainer}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onCreate={isAdmin ? onCreate : undefined}
      onUpdate={onUpdate}
      onDelete={onDelete}
      canCreate={actions.CreateMealPlan}
      canUpdate={actions.ModifyMealPlan}
      disableDelete={disableDelete}
      section={SECTION.MEAL_PLANS}
    />
  );
}

export default withAuthorizationVerifier(MealPlanCrud);
